import { z } from "zod";

/** Fully resolved color. `scheme` keeps provenance (e.g. "accent1") for the future editor. */
export const ColorSchema = z.object({
  hex: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  alpha: z.number().min(0).max(1).default(1),
  scheme: z.string().optional(),
});
export type Color = z.infer<typeof ColorSchema>;

export const CropSchema = z.object({ l: z.number(), t: z.number(), r: z.number(), b: z.number() });
export type Crop = z.infer<typeof CropSchema>;

export const FillSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("solid"), color: ColorSchema }),
  z.object({
    kind: z.literal("gradient"),
    stops: z.array(z.object({ pos: z.number().min(0).max(1), color: ColorSchema })).min(1),
    shape: z.enum(["linear", "radial", "rect"]),
    angle: z.number().optional(),
    scaled: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("image"),
    mediaId: z.string(),
    mode: z.enum(["stretch", "tile"]),
    crop: CropSchema.optional(),
    alpha: z.number().min(0).max(1).optional(),
    /** a:tile: offset (pt), scale (fraction) and alignment of the tiles */
    tile: z.object({ tx: z.number(), ty: z.number(), sx: z.number(), sy: z.number(), align: z.string() }).optional(),
  }),
  z.object({ kind: z.literal("pattern"), preset: z.string(), fg: ColorSchema, bg: ColorSchema }),
  z.object({ kind: z.literal("none") }),
]);
export type Fill = z.infer<typeof FillSchema>;

export const DashSchema = z.union([
  z.enum([
    "solid", "dot", "dash", "lgDash", "dashDot", "lgDashDot", "lgDashDotDot",
    "sysDash", "sysDot", "sysDashDot", "sysDashDotDot",
  ]),
  z.array(z.number()),
]);

export const LineEndSchema = z.object({
  type: z.enum(["none", "triangle", "stealth", "diamond", "oval", "arrow"]),
  w: z.enum(["sm", "med", "lg"]).default("med"),
  len: z.enum(["sm", "med", "lg"]).default("med"),
});
export type LineEnd = z.infer<typeof LineEndSchema>;

export const StrokeSchema = z.object({
  width: z.number().min(0),
  fill: FillSchema,
  dash: DashSchema.default("solid"),
  cap: z.enum(["flat", "round", "square"]).default("flat"),
  join: z.enum(["round", "bevel", "miter"]).default("round"),
  head: LineEndSchema.optional(),
  tail: LineEndSchema.optional(),
});
export type Stroke = z.infer<typeof StrokeSchema>;

export const EffectsSchema = z.object({
  shadow: z
    .object({ color: ColorSchema, blur: z.number(), dist: z.number(), angle: z.number(), kind: z.enum(["outer", "inner"]) })
    .optional(),
  glow: z.object({ color: ColorSchema, radius: z.number() }).optional(),
  softEdge: z.object({ radius: z.number() }).optional(),
});
export type Effects = z.infer<typeof EffectsSchema>;

export const TransformSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  rot: z.number().default(0),
  flipH: z.boolean().default(false),
  flipV: z.boolean().default(false),
});
export type Transform = z.infer<typeof TransformSchema>;

export const HyperlinkSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("url"), href: z.string() }),
  z.object({ kind: z.literal("slide"), pageId: z.string() }),
  z.object({ kind: z.literal("action"), action: z.string() }),
]);
export type Hyperlink = z.infer<typeof HyperlinkSchema>;
